import { Suspense, lazy } from 'react';
import { Navigate, useRoutes, useLocation, Outlet } from 'react-router-dom';
// layouts
import MainLayout from '../layouts/main';
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
// import RoleBasedGuard from '../guards/RoleBasedGuard';
// config
import { PATH_AFTER_LOGIN } from '../config';
// components
import LoadingScreen from '../components/LoadingScreen';
import BlankLayout from '../layouts/BankLayout';

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) =>
  (
    <Suspense fallback={<LoadingScreen loadable={true} />}>
      <Component {...props} />
    </Suspense>
  );

export default function Router() {
  return useRoutes([
    {
      path: 'register',
      element: (
        <GuestGuard>
          <Register />
        </GuestGuard>
      ),
    },
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: <DashboardLayout />,
          children: [
            {
              element: (
                <GuestGuard>
                  <Login />
                </GuestGuard>
              ),
              index: true,
            },
          ],
        },
        {
          path: 'register',
          element: <DashboardLayout />,
          children: [
            {
              element: (
                <GuestGuard>
                  <Register />
                </GuestGuard>
              ),
              index: true,
            },
          ],
        },
        // { path: 'login-unprotected', element: <Login /> },
        // { path: 'register-unprotected', element: <Register /> },
        { path: 'reset-password', element: <ResetPassword /> },
        { path: 'new-password', element: <NewPassword /> },
        { path: 'verify', element: <VerifyCode /> },
      ],
    },

    // Unified Dashboard Layout
    {
      path: '',
      element: <DashboardLayout />,
      children: [
        // { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
        { element: <Home />, index: true },
        { path: 'games/:categSlug', element: <GamesByCateg /> },
        { path: 'games/:categSlug/:companyId', element: <GamesByCateg /> },
        { path: 'tnc', element: <TermsAndConditions /> },

        // History Routes
        {
          path: 'history',
          children: [
            { path: 'bet', element: <GameHistory /> },
            { path: 'transaction', element: <TransactionHistory /> },
          ],
        },

        // User Routes
        {
          path: 'user',
          children: [
            { path: 'account', element: <AccountProfile /> },
            { path: 'banks', element: <MemberBankList /> },
            { path: 'faq', element: <Faq /> },
          ],
        },

        // Referral Route
        {
          path: 'referral',
          element: <Referral />,
        },

        // Bonus Route
        {
          path: 'vip',
          element: <Vip />,
        },

        {
          path: 'agent',
          element: <Agent />,
        },
        {
          path: 'favorite-game',
          element: <FavoriteGame />,
        },
        // Promotion Routes
        {
          path: 'promotion',
          children: [
            { element: <Promotion />, index: true },
            { path: 'view/:promotionId', element: <ViewPromotion /> },
          ],
        },
        {
          path: 'game-download',
          element: <Download />,
        },
        {
          path: 'responsible-gaming',
          element: <ResponsibleGaming />,
        },
        {
          path: 'privacy',
          element: <PrivacyPolicy />,
        },
        {
          path: 'about-us',
          element: <About />,
        },
        {
          path: 'social-links',
          children: [{ element: <SocialLinks />, index: true }],
        },

        // CustomPage Route
        {
          path: 'page',
          children: [{ path: ':customPage', element: <CustomPage /> }],
        },

        {
          path: 'deposit',
          children: [{ element: <Deposit />, index: true }],
        },
        {
          path: 'withdraw',
          children: [{ element: <Withdraw />, index: true }],
        },

        {
          path: 'cs',
          children: [{ element: <CustomerService />, index: true }],
        },

        // Auth Routes
        {
          path: '',
          element: (
            <AuthGuard>
              <Outlet />
            </AuthGuard>
          ),
          children: [
            {
              path: 'transfer',
              children: [
                {
                  index: true,
                  element: <Transfer />,
                },
              ],
            },
          ],
        },
      ],
    },

    {
      path: 'payStatus',
      element: <LogoOnlyLayout />,
      children: [{ path: ':paymentMethod', element: <PaymentStatus />, index: true }],
    },

    // AngPau
    {
      path: 'angpau',
      element: (
        // <AuthGuard>
        <AngPau />
        // </AuthGuard>
      ),
    },

    // Download Page
    {
      path: '',
      element: <BlankLayout />,
      children: [{ path: 'download', element: <DownloadPage /> }],
    },

    {
      path: '',
      element: <BlankLayout />,
      children: [{ path: 'download', element: <DownloadPage /> }],
    },

    {
      path: '',
      element: <Navigate to={PATH_AFTER_LOGIN} replace />,
    },

    // Main Routes
    {
      path: '*',
      element: <DashboardLayout />,
      children: [
        { path: 'coming-soon', element: <ComingSoon /> },
        { path: 'maintenance', element: <Maintenance /> },
        { path: '500', element: <Page500 /> },
        { path: '404', element: <Page404 /> },
        { path: '403', element: <Page403 /> },
        // { path: 'google9d00515fa23248b2.html', element: <></> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    // {
    //   path: '/',
    //   element: <MainLayout />,
    //   children: [
    //     { element: <HomePage />, index: true },
    //     { path: 'about-us', element: <About /> },
    //     { path: 'contact-us', element: <Contact /> },
    //     { path: 'faqs', element: <Faqs /> },
    //   ],
    // },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

// AUTHENTICATION
const Login = Loadable(lazy(() => import('../pages/auth/Login')));
const Register = Loadable(lazy(() => import('../views/visitor/Register')));
const ResetPassword = Loadable(lazy(() => import('../pages/auth/ResetPassword')));
const NewPassword = Loadable(lazy(() => import('../pages/auth/NewPassword')));
const VerifyCode = Loadable(lazy(() => import('../pages/auth/VerifyCode')));

// HOME
const Home = Loadable(lazy(() => import('../views/game/Home')));

// TERMS AND CONSITIONS
const TermsAndConditions = Loadable(lazy(() => import('../views/terms/TermsAndConditions')));

// GAMES
const GamesByCateg = Loadable(lazy(() => import('../views/game/GamesByCateg')));

// GAME HISTORY
const GameHistory = Loadable(lazy(() => import('../views/history/GameHistory')));

// DEPOSIT
const Deposit = Loadable(lazy(() => import('../layouts/dashboard/header/deposit/Deposit')));

// WITHDRAW
const Withdraw = Loadable(lazy(() => import('../layouts/dashboard/header/withdraw/Withdraw')));

// TRANSACTION HISTORY
const TransactionHistory = Loadable(lazy(() => import('../views/history/TransactionHistory')));

// USER
const AccountProfile = Loadable(lazy(() => import('../views/user/AccountProfile')));
const MemberBankList = Loadable(lazy(() => import('../views/user/MemberBankList')));
const About = Loadable(lazy(() => import('../views/user/About')));
const Faq = Loadable(lazy(() => import('../views/user/Faq')));

// INVITE
const Invite = Loadable(lazy(() => import('../views/share/Invite')));

// PROMOTION
const Promotion = Loadable(lazy(() => import('../views/promotion/Promotion')));
const ViewPromotion = Loadable(lazy(() => import('../views/promotion/ViewPromotion')));
const AngPau = Loadable(lazy(() => import('../views/promotion/AngPau')));

// PAYMENT STATS
const PaymentStatus = Loadable(lazy(() => import('../pages/PaymentStatus')));

// REFERRAL
const Referral = Loadable(lazy(() => import('../views/referral/Referral')));

// Bonus
const Vip = Loadable(lazy(() => import('../views/referral/Vip')));
const Agent = Loadable(lazy(() => import('../views/referral/Agent')));

// CUSTOM PAGE
const CustomPage = Loadable(lazy(() => import('../views/CustomPage')));

// DOWNLOAD PAGE
const DownloadPage = Loadable(lazy(() => import('../views/DownloadPage')));

// Social Links
const SocialLinks = Loadable(lazy(() => import('../views/social/SocialLinks')));

// Favorite Game
const FavoriteGame = Loadable(lazy(() => import('../views/game/FavoriteGame')));

// TRANSFER
const Transfer = Loadable(lazy(() => import('../views/transfer/Transfer')));

// CUSTOMER SERVICE
const CustomerService = Loadable(lazy(() => import('../views/cs/CustomerService')));

// DOWNLOAD PAGE
const Download = Loadable(lazy(() => import('../views/game/Download')));

// RESPONSIBLE GAMING PAGE
const ResponsibleGaming = Loadable(lazy(() => import('../views/game/ResponsibleGaming')));

// RESPONSIBLE GAMING PAGE
const PrivacyPolicy = Loadable(lazy(() => import('../views/privacy/PrivacyPolicy')));

// MAIN
const HomePage = Loadable(lazy(() => import('../pages/Home')));
const ComingSoon = Loadable(lazy(() => import('../pages/ComingSoon')));
const Maintenance = Loadable(lazy(() => import('../pages/Maintenance')));
const Page500 = Loadable(lazy(() => import('../pages/Page500')));
const Page403 = Loadable(lazy(() => import('../pages/Page403')));
const Page404 = Loadable(lazy(() => import('../pages/Page404')));
